import React from 'react';
import { isPlaceholder } from '@/lib/bracket/tree';
import { slotKey } from '@/lib/bracket/custom';
import { fitText, measure } from '@/lib/bracket/text';
import { CARD_TONES, COLORS, rr, shortDate } from './theme';
import { sideRuns } from '@/lib/cricket';

export const canEditMatch = (m) => !!m && !isPlaceholder(m.team_a) && !isPlaceholder(m.team_b);

const metaText = (m, showCourt) => [m.date && shortDate(m.date), m.time, showCourt && m.court ? `Ct ${m.court}` : null]
  .filter(Boolean).join(' · ');

const abbreviate = (s) => s.replace(/^Winner of /, 'W: ').replace(/^Loser of /, 'L: ');

// Score box text: "FF", runs, or cricket "192/4" when wickets were entered.
const scoreText = (m, slot) => {
  const side = slot ? 'b' : 'a';
  if (m.forfeit_team === 'both' || m.forfeit_team === side) return 'FF';
  return sideRuns(m, side);
};

// Widen the score box just enough for cricket scorelines; both rows share the width.
const scoreWidth = (m, M) => Math.max(M.fs * 2.2, ...[0, 1].map((s) => measure(scoreText(m, s), M.fs * 0.95, 700) + M.fs * 0.7));

function TeamRow({ m, slot, y, w, M, r, logoHref, clipId, blank, nameFs, writeIn, onSlot, sw }) {
  const id = slot ? m.team_b : m.team_a;
  const won = !!m.winner && m.winner === id;
  const name = slot ? m.team_b_name : m.team_a_name;
  const ph = isPlaceholder(id) && !(writeIn && name);
  const lost = !!m.winner && !won && !ph;
  const pad = M.fs * 0.5;
  const ls = M.rowH * 0.6;
  const showLogo = !!logoHref && !blank;
  const nx = pad + (showLogo ? ls + M.fs * 0.4 : 0);
  const weight = won ? 700 : ph ? 400 : 600;
  const maxW = w - nx - sw - pad * 1.2;
  const raw = blank ? '' : (name || (writeIn ? '' : 'TBD'));
  const base = nameFs || M.fs;
  let t = fitText(raw, maxW, base, weight, 0.85);
  if (ph && t.text.endsWith('…')) t = fitText(abbreviate(raw), maxW, base, weight, 0.85);
  const cy = y + M.rowH / 2;
  const sx = w - sw - pad * 0.45;
  const scoreTxt = scoreText(m, slot);
  const fill = won ? COLORS.navy : lost ? '#64748B' : ph ? COLORS.muted : COLORS.ink;
  const key = onSlot ? slotKey(m, slot) : null;
  return (
    <g x-excluded="true">
      {won && <path d={rr(0, y, w, M.rowH, slot ? [0, 0, r, r] : [0, 0, 0, 0])} fill="#E9F7EF" />}
      {showLogo && (
        <>
          <clipPath x-excluded="true" id={clipId}><circle cx={pad + ls / 2} cy={cy} r={ls / 2} /></clipPath>
          <image href={logoHref} x={pad} y={cy - ls / 2} width={ls} height={ls} preserveAspectRatio="xMidYMid slice" clipPath={`url(#${clipId})`} />
        </>
      )}
      <text x-excluded="true" x={nx} y={cy + t.size * 0.34} fontSize={t.size} fontWeight={weight} fill={fill}>{t.text}</text>
      <rect x={sx} y={y + M.rowH * 0.17} width={sw} height={M.rowH * 0.66} rx={M.fs * 0.22}
        fill={won ? COLORS.navy : '#F4F6FA'} stroke={won ? COLORS.navy : '#DCE2EC'} strokeWidth={0.4} />
      {scoreTxt && (
        <text x-excluded="true" x={sx + sw / 2} y={cy + M.fs * 0.33} textAnchor="middle" fontSize={M.fs * 0.95} fontWeight={700}
          fill={won ? '#FFFFFF' : COLORS.slate}>{scoreTxt}</text>
      )}
      {key && (
        <rect className="bk-slot" data-bk-slot={key} data-testid={`bracket-slot-${key}`} x={0} y={y} width={sx} height={M.rowH} fill="transparent"
          onClick={(e) => { e.stopPropagation(); onSlot(key, e.currentTarget); }} />
      )}
    </g>
  );
}

function StatsIcon({ href, x, y, s, matchId }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" data-screen-only="true" data-testid={`bracket-stats-${matchId}`}
      onClick={(e) => e.stopPropagation()}>
      <title>Game stats &amp; box score</title>
      <rect x={x} y={y} width={s} height={s} rx={s * 0.22} fill="#FFFFFF" fillOpacity={0.95} />
      <g fill="#D97706">
        <rect x={x + s * 0.2} y={y + s * 0.5} width={s * 0.15} height={s * 0.3} rx={s * 0.04} />
        <rect x={x + s * 0.42} y={y + s * 0.22} width={s * 0.15} height={s * 0.58} rx={s * 0.04} />
        <rect x={x + s * 0.64} y={y + s * 0.38} width={s * 0.15} height={s * 0.42} rx={s * 0.04} />
      </g>
    </a>
  );
}

function MetaStrip({ m, card, M, r, tone, showCourt, statsHref }) {
  const pad = M.fs * 0.5;
  const mfs = M.fs * 0.68;
  const icon = statsHref ? M.metaH * 0.82 : 0;
  const right = card.w - pad - (icon ? icon + pad * 0.5 : 0);
  const label = fitText(card.label || m.match_label || (m.stage || '').replace('PO_', ''), card.w * 0.5, mfs, 700, 0.85);
  const used = measure(label.text, label.size, 700);
  const meta = fitText(metaText(m, showCourt), right - pad * 2 - used, mfs, 400, 0.85);
  const by = M.metaH / 2 + mfs * 0.36;
  return (
    <g x-excluded="true">
      <path d={rr(0, 0, card.w, M.metaH, [r, r, 0, 0])} fill={tone.strip} />
      <text x-excluded="true" x={pad} y={by} fontSize={label.size} fontWeight={700} fill={tone.stripText}>{label.text}</text>
      {meta.text && <text x-excluded="true" x={right} y={by} textAnchor="end" fontSize={meta.size} fontWeight={400} fill={tone.stripText} fillOpacity={0.8}>{meta.text}</text>}
      {statsHref && <StatsIcon href={statsHref} x={card.w - pad * 0.6 - icon} y={(M.metaH - icon) / 2} s={icon} matchId={m.id} />}
    </g>
  );
}

export const MatchCardSvg = ({ card, M, logoUrl, onSelect, blank, showCourt, clipBase, noIds, nameFs, writeIn, onSlot, canEdit, statsHref }) => {
  const { match: m, x, y, w, h } = card;
  const tone = CARD_TONES[card.tone] || CARD_TONES.normal;
  const r = Math.min(M.fs * 0.45, 4);
  const editable = !!onSelect && (canEdit || canEditMatch)(m);
  const divY = M.metaH + M.rowH;
  const badge = M.fs * 1.5;
  const sw = scoreWidth(m, M);
  return (
    <g x-excluded="true" transform={`translate(${x.toFixed(2)} ${y.toFixed(2)})`} data-testid={noIds ? undefined : `bracket-match-${m.id}`}
      onClick={editable ? () => onSelect(m) : undefined} className={editable ? 'bk-editable' : undefined}>
      <rect width={w} height={h} rx={r} fill="#FFFFFF" />
      {M.metaH > 0 && <MetaStrip m={m} card={card} M={M} r={r} tone={tone} showCourt={showCourt} statsHref={statsHref} />}
      {[0, 1].map((slot) => (
        <TeamRow key={slot} m={m} slot={slot} y={M.metaH + M.rowH * slot} w={w} M={M} r={r} blank={blank} nameFs={nameFs} writeIn={writeIn} onSlot={onSlot} sw={sw}
          logoHref={logoUrl?.(slot ? m.team_b : m.team_a)} clipId={`${clipBase}-${m.id}-${slot}`} />
      ))}
      <line x1={0} x2={w} y1={divY} y2={divY} stroke="#E5E9F0" strokeWidth={0.5} />
      <rect className="bk-outline" width={w} height={h} rx={r} fill="none" stroke={tone.stroke} strokeWidth={tone.sw} />
      {statsHref && M.metaH === 0 && <StatsIcon href={statsHref} x={w - badge * 0.6} y={-badge * 0.4} s={badge} matchId={m.id} />}
    </g>
  );
};
